import React, { useState } from 'react';
import { CheckCircle2, XCircle, Loader2, ArrowLeft, ArrowRight, Zap } from 'lucide-react';
import { onboardingApi } from '../../services/api';
import type { OnboardingState } from '../../types';

interface Props {
  state: OnboardingState;
  setConnection: (conn: OnboardingState['connection'], tested: boolean) => void;
  onNext: () => void;
  onBack: () => void;
}

/* Field definitions per connector type */
const commonFields = [
  { key: 'host', label: 'Host', placeholder: 'hostname or IP' },
  { key: 'port', label: 'Port', placeholder: '5432', type: 'number' },
  { key: 'database', label: 'Database', placeholder: 'my_database' },
  { key: 'username', label: 'Username', placeholder: 'username' },
  { key: 'password', label: 'Password', placeholder: '••••••••', type: 'password' },
];

const snowflakeFields = [
  { key: 'account', label: 'Account', placeholder: 'org-account' },
  { key: 'warehouse', label: 'Warehouse', placeholder: 'COMPUTE_WH' },
  { key: 'database', label: 'Database', placeholder: 'my_database' },
  { key: 'role', label: 'Role', placeholder: 'SYSADMIN (optional)' },
  { key: 'username', label: 'Username', placeholder: 'username' },
  { key: 'password', label: 'Password', placeholder: '••••••••', type: 'password' },
];

const ConnectionForm: React.FC<Props> = ({ state, setConnection, onNext, onBack }) => {
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<'success' | 'fail' | null>(null);
  const [testError, setTestError] = useState('');

  const fields = state.connectorType === 'snowflake' ? snowflakeFields : commonFields;
  const conn = state.connection;

  const updateField = (key: string, value: string) => {
    setConnection({ ...conn, [key]: key === 'port' ? Number(value) : value }, false);
    setTestResult(null);
  };

  const handleLoadTestCreds = async () => {
    try {
      const res = await onboardingApi.loadTestCredentials();
      if (res.data.status === 'success' && res.data.payload) {
        setConnection({ ...conn, ...res.data.payload }, false);
        setTestResult(null);
      }
    } catch {
      /* ignore */
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    setTestError('');
    try {
      const res = await onboardingApi.testConnection(conn as any);
      if (res.data.status === 'success') {
        setTestResult('success');
        setConnection(conn, true);
      } else {
        setTestResult('fail');
        setTestError(res.data.message || 'Connection failed');
      }
    } catch (err: any) {
      setTestResult('fail');
      setTestError(err.response?.data?.message || err.message || 'Connection failed');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div>
      <h2 className="text-lg font-semibold text-cargo-text mb-4">
        Connect to {state.connectorType}
      </h2>
      <div className="bg-cargo-surface border border-cargo-border rounded-xl p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {fields.map((f) => (
            <div key={f.key}>
              <label className="block text-sm font-medium text-cargo-muted mb-1.5">
                {f.label}
              </label>
              <input
                type={f.type || 'text'}
                placeholder={f.placeholder}
                value={(conn as any)[f.key] || ''}
                onChange={(e) => updateField(f.key, e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg bg-cargo-panel border border-cargo-border
                           text-cargo-text placeholder-cargo-muted/50 text-sm
                           focus:border-cargo-accent focus:ring-1 focus:ring-cargo-accent outline-none"
              />
            </div>
          ))}
        </div>

        {/* Test connection */}
        <div className="flex items-center gap-3 pt-2">
          <button
            onClick={handleLoadTestCreds}
            className="flex items-center gap-2 bg-cargo-accent/10 hover:bg-cargo-accent/20 text-cargo-accent
                       font-medium py-2.5 px-5 rounded-lg transition-colors text-sm"
          >
            <Zap className="w-4 h-4" /> Load Test Credentials
          </button>
          <button
            onClick={handleTest}
            disabled={testing}
            className="flex items-center gap-2 bg-cargo-panel hover:bg-cargo-hover text-cargo-text
                       font-medium py-2.5 px-5 rounded-lg transition-colors disabled:opacity-50"
          >
            {testing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              'Test Connection'
            )}
          </button>
          {testResult === 'success' && (
            <span className="flex items-center gap-1.5 text-cargo-success text-sm">
              <CheckCircle2 className="w-4 h-4" /> Connected
            </span>
          )}
          {testResult === 'fail' && (
            <span className="flex items-center gap-1.5 text-cargo-error text-sm">
              <XCircle className="w-4 h-4" /> {testError}
            </span>
          )}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between mt-6">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-cargo-muted hover:text-cargo-text transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button
          onClick={onNext}
          disabled={!state.connectionTested}
          className="flex items-center gap-2 bg-cargo-accent hover:bg-cargo-accent-hover text-white
                     font-medium py-2.5 px-5 rounded-lg transition-colors disabled:opacity-30"
        >
          Next <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default ConnectionForm;
